import { PageLoading } from '@gc-bridge/components/PageLoading';
import { useMaintenanceMode } from '@gc-bridge/hooks/useMaintenanceMode';
import {
  registerMaintenanceModeInterceptor,
  rewriteRequestsPath,
} from '@gc-bridge/interceptors';
import { authProvider, getPluginSlots } from '@gc-bridge/services';
import { useCurrentUserStore } from '@gc-bridge/stores/currentUser';
import { getRemoteExposedApp, isRemoteInMaintenanceMode } from '@gcportal/base';
import { AxiosInstance } from 'axios';
import React, { Suspense, useMemo } from 'react';
import { Maintenance } from '../pages/Maintenance';

const getElement = (load: () => Promise<unknown>, lazy: boolean, element?: unknown) => {
  if (!lazy) {
    load();
    return element;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return React.lazy(() => load() as any);
  }
};

interface Props {
  remoteName: string;
}

export function RemoteRouteProvider({ remoteName }: Props) {
  const { openMaintenanceDialog, closeMaintenanceDialog } = useMaintenanceMode();

  const { user: currentUser } = useCurrentUserStore();

  const remoteApp = useMemo(() => getRemoteExposedApp(remoteName), [remoteName]);

  const remoteIsInMaintenanceMode = useMemo(
    () => remoteApp && isRemoteInMaintenanceMode(remoteApp, currentUser?.groups),
    [remoteApp, currentUser?.groups]
  );

  const Element = useMemo(
    () =>
      remoteApp && !remoteIsInMaintenanceMode
        ? getElement(remoteApp.load, remoteApp.lazy, remoteApp.element)
        : undefined,
    [remoteApp, remoteIsInMaintenanceMode]
  );

  // Plugin slots are resolved once during setup. This component must not
  // subscribe to the config store: re-rendering it on every config refresh
  // remounts the embedded remote and the user loses their work.
  const injectedPlugins = useMemo(() => getPluginSlots(remoteName), [remoteName]);

  if (!remoteApp) {
    return null;
  }

  if (remoteIsInMaintenanceMode) {
    return <Maintenance remoteAppName={remoteApp.remoteDisplayName} />;
  }

  return (
    <Suspense fallback={<PageLoading />}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-expect-error */}
      <Element
        rewriteRequestsPath={rewriteRequestsPath}
        registerMaintenanceModeInterceptor={(axios: AxiosInstance) =>
          registerMaintenanceModeInterceptor(
            axios,
            remoteName,
            openMaintenanceDialog,
            closeMaintenanceDialog
          )
        }
        getMaintenanceFlag={() => {
          // Not using already calculated remoteIsInMaintenanceMode to ensure
          // the remote will always get the latest status
          const exposedApp = getRemoteExposedApp(remoteName);

          return exposedApp && isRemoteInMaintenanceMode(exposedApp, currentUser?.groups);
        }}
        {...(remoteApp.shouldPassAuthFunction ? { shellProvidedAuth: authProvider } : {})}
        {...(remoteApp.config ? { config: remoteApp.config } : {})}
        {...(injectedPlugins ? { injectedPlugins } : {})}
      />
    </Suspense>
  );
}
